/**
 * Cities catalog for quotes
 */

import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Icon, Input } from "@/components/ui";
import { citiesService } from "@/services/citiesService";
import { provincesService } from "@/services/provincesService";
import { notifyAction } from "@/utils";
import type { APICity, APICityRequest } from "@/types";
import styles from "./CitiesPage.module.css";

interface CityContact {
  id: string;
  name: string;
  province: number;
  province_name: string;
  email?: string;
}

interface CityFormState {
  name: string;
  province: string;
  email: string;
}

interface ProvinceOption {
  id: number;
  name: string;
}

const PAGE_SIZE = 20;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const mapAPICity = (c: APICity): CityContact => ({
  id: String(c.id),
  name: c.name,
  province: c.province,
  province_name: c.province_name,
  email: c.email,
});

export function CitiesPage(): ReactElement {
  const [cities, setCities] = useState<CityContact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [provinceOptions, setProvinceOptions] = useState<ProvinceOption[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(true);
  const [provinceFilter, setProvinceFilter] = useState<number | null>(null);
  const [provinceSearch, setProvinceSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [formState, setFormState] = useState<CityFormState>({
    name: "",
    province: "",
    email: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<CityFormState>>({});
  const [editingCityId, setEditingCityId] = useState<string | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedCity, setSelectedCity] = useState<CityContact | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadCities = async (targetPage: number, province: number | null) => {
    try {
      setIsLoading(true);
      const res = await citiesService.list({
        page: targetPage,
        page_size: PAGE_SIZE,
        ...(province !== null ? { province } : {}),
      });
      const mapped = res.results.map(mapAPICity);
      setCities(mapped);
      setTotalCount(res.count);

      setSelectedCity((prev) => {
        if (prev) return prev;
        return mapped[0] ?? null;
      });
    } catch (err) {
      console.error("Error loading cities:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadProvinceOptions = async () => {
    try {
      setIsLoadingProvinces(true);
      const all: ProvinceOption[] = [];
      let targetPage = 1;
      let hasNext = true;

      while (hasNext) {
        const res = await provincesService.list({
          page: targetPage,
          page_size: 100,
        });
        all.push(
          ...res.results.map((province) => ({
            id: province.id,
            name: province.name,
          })),
        );
        hasNext = res.next !== null;
        targetPage += 1;
      }

      setProvinceOptions(all);
    } catch (err) {
      console.error("Error loading province options:", err);
    } finally {
      setIsLoadingProvinces(false);
    }
  };

  useEffect(() => {
    void loadProvinceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadCities(page, provinceFilter);
  }, [page, provinceFilter]);

  const filteredCities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return cities;
    return cities.filter((city) => city.name.toLowerCase().includes(term));
  }, [cities, searchTerm]);

  const resetForm = () => {
    setFormState({ name: "", province: "", email: "" });
    setFormErrors({});
    setEditingCityId(null);
    setProvinceSearch("");
  };

  const openCreateForm = () => {
    resetForm();
    setFormVisible(true);
  };

  const openEditForm = (city: CityContact) => {
    setFormState({
      name: city.name,
      province: String(city.province),
      email: city.email ?? "",
    });
    setFormErrors({});
    setEditingCityId(city.id);
    setFormVisible(true);
  };

  const closeForm = () => {
    resetForm();
    setFormVisible(false);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = formState.name.trim();
    const trimmedEmail = formState.email.trim();
    const provinceNum = parseInt(formState.province, 10);

    const errors: Partial<CityFormState> = {};
    if (!trimmedName) errors.name = "Introduce un nombre";
    if (!formState.province || isNaN(provinceNum)) {
      errors.province = "Selecciona una provincia válida";
    }
    if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
      errors.email = "Correo no válido";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: APICityRequest = {
      name: trimmedName,
      province: provinceNum,
      ...(trimmedEmail ? { email: trimmedEmail } : {}),
    };

    try {
      if (editingCityId) {
        const updatedCity = await citiesService.update(
          Number(editingCityId),
          payload,
        );
        const mapped = mapAPICity(updatedCity);
        setCities((prev) => prev.map((c) => (c.id === mapped.id ? mapped : c)));
        if (selectedCity?.id === mapped.id) setSelectedCity(mapped);
      } else {
        const newCity = await citiesService.create(payload);
        const mapped = mapAPICity(newCity);
        setCities((prev) => [...prev, mapped]);
        setTotalCount((prev) => prev + 1);
      }
      closeForm();
    } catch (err) {
      console.error("Error saving city:", err);
    }
  };

  const deleteCity = async (city: CityContact) => {
    const deletedId = city.id;
    try {
      await citiesService.remove(Number(deletedId));
      setCities((prev) => prev.filter((c) => c.id !== deletedId));
      setTotalCount((prev) => prev - 1);
      if (selectedCity?.id === deletedId) setSelectedCity(null);
      if (editingCityId === deletedId) closeForm();
    } catch (err) {
      console.error("Error deleting city:", err);
    }
  };

  const requestDelete = (city: CityContact) => {
    notifyAction({
      title: "Eliminar ciudad",
      description: `Se eliminará \"${city.name}\" del catálogo.`,
      buttonTitle: "Eliminar",
      onClick: () => {
        void deleteCity(city);
      },
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <p className={styles.breadcrumb}>Catálogo · Ciudades</p>
            <h1 className={styles.title}>Ciudades operativas</h1>
            <p className={styles.subtitle}>
              Ciudades disponibles para el selector de presupuestos.
            </p>
          </div>
          <Button onClick={openCreateForm} className={styles.primaryAction}>
            <Icon name="plus" size={18} />
            Nueva ciudad
          </Button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Buscar ciudad"
              value={searchTerm}
              onChange={handleSearchChange}
              leftElement={<Icon name="search" size={16} />}
              fullWidth
            />
          </div>

          {/* Province filter — populated from loaded data */}
          {provinceOptions.length > 0 && (
            <select
              className={styles.provinceSelect}
              value={provinceFilter ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                setPage(1);
                setProvinceFilter(val === "" ? null : Number(val));
              }}
            >
              <option value="">Todas las provincias</option>
              {provinceOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <span className={styles.resultCount}>
            {isLoading ? "…" : `${totalCount} ciudades`}
          </span>
        </div>

        <div className={styles.content}>
          <div className={styles.listSection}>
            {isLoading ? (
              <div className={styles.emptyList}>
                <p>Cargando ciudades...</p>
              </div>
            ) : filteredCities.length > 0 ? (
              <>
                {filteredCities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    className={`${styles.cityItem} ${selectedCity?.id === city.id ? styles.activeItem : ""}`}
                    onClick={() => setSelectedCity(city)}
                  >
                    <div className={styles.cityInfo}>
                      <p className={styles.cityName}>{city.name}</p>
                      <p className={styles.cityEmail}>
                        {city.province_name}
                        {city.email ? ` · ${city.email}` : ""}
                      </p>
                    </div>
                    <div className={styles.itemActions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditForm(city);
                        }}
                        title="Editar ciudad"
                      >
                        <Icon name="edit" size={16} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          requestDelete(city);
                        }}
                        title="Eliminar ciudad"
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </button>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      ‹
                    </button>
                    <span>
                      {page} / {totalPages}
                    </span>
                    <button
                      className={styles.paginationBtn}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                    >
                      ›
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyList}>
                <Icon name="mapPin" size={20} />
                <p>No hay ciudades que coincidan con el filtro.</p>
              </div>
            )}
          </div>

          <div className={styles.panel}>
            {formVisible ? (
              <form
                className={styles.formCard}
                onSubmit={(e) => void handleSubmit(e)}
              >
                <div className={styles.formHeader}>
                  <Icon name="mapPin" size={24} />
                  <div>
                    <h3>{editingCityId ? "Editar ciudad" : "Nueva ciudad"}</h3>
                    <p>Nombre, provincia y correo de contacto opcional.</p>
                  </div>
                </div>
                <Input
                  label="Nombre de la ciudad"
                  value={formState.name}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Ej. Málaga"
                  error={formErrors.name}
                  required
                  fullWidth
                />
                <div>
                  <label htmlFor="province-select">Provincia</label>
                  {!isLoadingProvinces && provinceOptions.length > 0 && (
                    <input
                      type="text"
                      className={styles.provinceSearchInput}
                      placeholder="Buscar provincia..."
                      value={provinceSearch}
                      onChange={(e) => setProvinceSearch(e.target.value)}
                      autoComplete="off"
                    />
                  )}
                  <select
                    id="province-select"
                    className={styles.provinceSelect}
                    value={formState.province}
                    disabled={isLoadingProvinces}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        province: event.target.value,
                      }))
                    }
                  >
                    {isLoadingProvinces ? (
                      <option value="">Cargando provincias...</option>
                    ) : provinceOptions.length === 0 ? (
                      <option value="">No hay provincias disponibles</option>
                    ) : (
                      <>
                        <option value="">Selecciona una provincia</option>
                        {provinceOptions
                          .filter((p) =>
                            provinceSearch.trim() === ""
                              ? true
                              : p.name
                                  .toLowerCase()
                                  .includes(
                                    provinceSearch.trim().toLowerCase(),
                                  ),
                          )
                          .map((p) => (
                            <option key={p.id} value={String(p.id)}>
                              {p.name}
                            </option>
                          ))}
                      </>
                    )}
                  </select>
                  {formErrors.province && (
                    <span role="alert">{formErrors.province}</span>
                  )}
                </div>
                <Input
                  label="Correo de contacto (opcional)"
                  type="email"
                  value={formState.email}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                  placeholder="ciudad@biosam.es"
                  error={formErrors.email}
                  helperText="Dirección que recibirá los presupuestos generados."
                  fullWidth
                />
                <div className={styles.formActions}>
                  <Button type="button" variant="ghost" onClick={closeForm}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingCityId ? "Guardar cambios" : "Crear ciudad"}
                  </Button>
                </div>
              </form>
            ) : selectedCity ? (
              <div className={styles.detailCard}>
                <div className={styles.detailHeader}>
                  <p className={styles.detailEyebrow}>Ciudad seleccionada</p>
                  <h3>{selectedCity.name}</h3>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.fieldLabel}>Provincia</span>
                  <p className={styles.fieldValue}>
                    {selectedCity.province_name}
                  </p>
                </div>
                {selectedCity.email && (
                  <div className={styles.detailField}>
                    <span className={styles.fieldLabel}>Correo</span>
                    <p className={styles.fieldValue}>{selectedCity.email}</p>
                  </div>
                )}
                <Button
                  variant="secondary"
                  onClick={() => openEditForm(selectedCity)}
                >
                  Editar ciudad
                </Button>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Icon name="mapPin" size={32} />
                <p>Selecciona una ciudad de la lista o crea una nueva.</p>
                <Button variant="secondary" onClick={openCreateForm}>
                  Nueva ciudad
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CitiesPage;
